import { isFunction } from './function';
import type { Optional } from './object';
import { isNotNullOrUndefined } from './object';

/**
 * Returns a new value based on the provided value. If the provided value is null or undefined, it returns undefined.
 * If the second argument is a function, it will be called with the provided value as an argument.
 * If the second argument is not a function, it will be returned as is.
 *
 * @template Value - The type of the value to check.
 * @template NewValue - The type of the new value to return.
 * @param {Optional<Value>} value - The value to check.
 * @param {(value: Value) => NewValue} newValueCallback - A function to generate the new value.
 * @param defaultNoValue - The default value to return if the provided value is null or undefined.
 * @returns {NewValue | undefined} - The new value, or undefined if the provided value is null or undefined.
 */
export function withNewOptionalValue<Value, NewValue, NoValue extends undefined>(value: Optional<Value>, newValueCallback: (value: Value) => NewValue, defaultNoValue?: NoValue): NewValue | NoValue;
export function withNewOptionalValue<Value, NewValue, NoValue extends null>(value: Optional<Value>, newValueCallback: (value: Value) => NewValue, defaultNoValue?: NoValue): NewValue | NoValue;

/**
 * Returns a new value based on the provided value. If the provided value is null or undefined, it returns undefined.
 * If the second argument is a function, it will be called with the provided value as an argument.
 * If the second argument is not a function, it will be returned as is.
 *
 * @template Value - The type of the value to check.
 * @template NewValue - The type of the new value to return.
 * @param {Optional<Value>} value - The value to check.
 * @param {NewValue} newValue - The new value to return.
 * @param defaultNoValue - The default value to return if the provided value is null or undefined.
 * @returns {NewValue | undefined} - The new value, or undefined if the provided value is null or undefined.
 */
export function withNewOptionalValue<Value, NewValue, NoValue extends undefined>(value: Optional<Value>, newValue: NewValue, defaultNoValue?: NoValue): NewValue | NoValue;
export function withNewOptionalValue<Value, NewValue, NoValue extends null>(value: Optional<Value>, newValue: NewValue, defaultNoValue?: NoValue): NewValue | NoValue;
export function withNewOptionalValue<Value, NewValue, NoValue extends undefined | null>(value: Optional<Value>, newValueOption: NewValue | ((value: Value) => NewValue), defaultNoValue: NoValue): NewValue | NoValue {
  if (isFunction(newValueOption)) {
    return isNotNullOrUndefined(value) ? newValueOption(value) : defaultNoValue;
  }

  return isNotNullOrUndefined(value) ? newValueOption : defaultNoValue;
}